import { useMemo } from 'react';
import './markdown.css';
import { MarkdownRendererProps } from '@/types';

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  const formattedContent = useMemo(() => {
    return parseMarkdown(content);
  }, [content]);

  return (
    <div 
      className={`markdown-content ${className}`}
      dangerouslySetInnerHTML={{ __html: formattedContent }}
    />
  );
}

function parseMarkdown(markdown: string): string {
  let html = markdown;

  // Escape HTML first to prevent XSS
  html = html.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Code blocks (```language\ncode\n```)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    const languageLabel = lang ? `<span class="text-xs text-muted-foreground absolute top-2 right-2">${lang}</span>` : '';
    return `<pre class="relative bg-muted/80 rounded-lg p-4 overflow-x-auto my-3 border border-border/50">${languageLabel}<code class="text-sm font-mono block">${code.trim()}</code></pre>`;
  });

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code class="bg-muted/80 px-1.5 py-0.5 rounded text-sm font-mono border border-border/30">$1</code>');

  // Bold (**text** or __text__) - must come before italic
  html = html.replace(/\*\*([^\*]+)\*\*/g, '<strong class="font-bold text-foreground">$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong class="font-bold text-foreground">$1</strong>');

  // Italic (*text* or _text_)
  html = html.replace(/\*([^\*]+)\*/g, '<em class="italic">$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em class="italic">$1</em>');

  // Strikethrough (~~text~~)
  html = html.replace(/~~([^~]+)~~/g, '<del class="line-through opacity-70">$1</del>');

  // Headers (must be at start of line)
  html = html.replace(/^### (.+)$/gim, '<h3 class="text-base font-bold mt-4 mb-2 text-foreground">$1</h3>');
  html = html.replace(/^## (.+)$/gim, '<h2 class="text-lg font-bold mt-4 mb-2 text-foreground">$1</h2>');
  html = html.replace(/^# (.+)$/gim, '<h1 class="text-xl font-bold mt-4 mb-2 text-foreground">$1</h1>');

  // Horizontal rule
  html = html.replace(/^---$/gim, '<hr class="my-4 border-border" />');

  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors" target="_blank" rel="noopener noreferrer">$1</a>');

  // Blockquotes (must be at start of line)
  html = html.replace(/^&gt; (.+)$/gim, '<blockquote class="border-l-4 border-primary/30 pl-3 py-1 my-2 text-muted-foreground italic bg-muted/30 rounded-r">$1</blockquote>');

  // Process lists (more robust)
  const lines = html.split('\n');
  const processed: string[] = [];
  let inUl = false;
  let inOl = false;

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();
    
    // Unordered list
    if (trimmedLine.match(/^[*-] (.+)$/)) {
      if (!inUl) {
        processed.push('<ul class="list-disc list-inside my-2 space-y-1 ml-4">');
        inUl = true;
      }
      const content = trimmedLine.replace(/^[*-] /, '');
      processed.push(`<li class="text-sm">${content}</li>`);
    }
    // Ordered list
    else if (trimmedLine.match(/^\d+\. (.+)$/)) {
      if (!inOl) {
        processed.push('<ol class="list-decimal list-inside my-2 space-y-1 ml-4">');
        inOl = true;
      }
      const content = trimmedLine.replace(/^\d+\. /, '');
      processed.push(`<li class="text-sm">${content}</li>`);
    }
    else {
      // Close any open lists
      if (inUl) {
        processed.push('</ul>');
        inUl = false;
      }
      if (inOl) {
        processed.push('</ol>');
        inOl = false;
      }
      processed.push(line);
    }
  });

  // Close any remaining open lists
  if (inUl) processed.push('</ul>');
  if (inOl) processed.push('</ol>');

  html = processed.join('\n');

  // Paragraphs - double line breaks create new paragraphs
  html = html.replace(/\n\n+/g, '</p><p class="my-2">');
  html = '<p class="my-2">' + html + '</p>';

  // Single line breaks
  html = html.replace(/\n/g, '<br />');

  // Clean up empty paragraphs
  html = html.replace(/<p class="my-2"><\/p>/g, '');
  html = html.replace(/<p class="my-2">\s*<\/p>/g, '');

  return html;
}
